import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { InfoCircle, Profile2User } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { FilterChip, PageHeader, SearchField } from "@/components/Molecules";
import {
  CustomerDetailDrawer,
  CustomerStatsRow,
  CustomersTable,
  SefanoPagination,
} from "@/components/Organisms";
import { PageTemplate } from "@/components/Templates/Page";
import type { CustomerFilter } from "./Customers.template.types";
import type { CustomersTemplateProps } from "./Customers.template.types";
import * as styles from "./Customers.template.styles";

export type { CustomersTemplateProps } from "./Customers.template.types";

const FILTER_CHIPS: Array<{ key: CustomerFilter; labelKey: string }> = [
  { key: "all", labelKey: "filters.all" },
  { key: "repeat", labelKey: "filters.repeat" },
  { key: "new", labelKey: "filters.new" },
];

export function CustomersTemplate({
  displayCustomers,
  meta,
  stats,
  page,
  setPage,
  search,
  handleSearchChange,
  customerFilter,
  setCustomerFilter,
  selectedCustomerId,
  setSelectedCustomerId,
  handleCloseDrawer,
  isLoading,
  isStatsLoading,
  isEmpty,
  paginationLabel,
}: CustomersTemplateProps) {
  const { t } = useTranslation("customers");
  const theme = useTheme();

  return (
    <PageTemplate>
      <PageHeader title={t("title")} subtitle={t("subtitle")} />

      {!isEmpty || isLoading ? (
        <>
          <CustomerStatsRow stats={stats} isLoading={isStatsLoading} />

          <Box sx={styles.infoBanner}>
            <InfoCircle size={20} variant="Bold" color={theme.palette.primary.main} />
            <Typography sx={styles.infoBannerText}>{t("infoBanner")}</Typography>
          </Box>

          <Box sx={styles.contentCard}>
            <Box sx={styles.toolbar}>
              <Box sx={styles.toolbarFilters}>
                {FILTER_CHIPS.map(({ key, labelKey }) => (
                  <FilterChip
                    key={key}
                    label={t(labelKey)}
                    active={customerFilter === key}
                    onClick={() => setCustomerFilter(key)}
                  />
                ))}
              </Box>
              <Box sx={styles.searchWrap}>
                <SearchField
                  placeholder={t("searchPlaceholder")}
                  value={search}
                  onChange={handleSearchChange}
                  fieldSize="compact"
                />
              </Box>
            </Box>

            <CustomersTable
              customers={displayCustomers}
              isLoading={isLoading}
              onRowClick={setSelectedCustomerId}
            />

            {displayCustomers.length === 0 && !isLoading ? (
              <Typography sx={styles.noResults}>{t("noResults")}</Typography>
            ) : null}

            {meta && meta.last_page > 1 ? (
              <Box sx={styles.paginationFooter}>
                <Typography sx={styles.paginationCount}>{paginationLabel}</Typography>
                <SefanoPagination
                  count={meta.last_page}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                />
              </Box>
            ) : meta ? (
              <Typography sx={styles.paginationCount}>{paginationLabel}</Typography>
            ) : null}
          </Box>
        </>
      ) : (
        <Box sx={styles.emptyStateCard}>
          <Box sx={styles.emptyStateIconWrap}>
            <Profile2User size={60} variant="Bold" color={theme.palette.primary.main} />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {t("emptyTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={styles.emptyDescription}>
            {t("emptyDescription")}
          </Typography>
        </Box>
      )}

      <CustomerDetailDrawer
        open={selectedCustomerId !== null}
        customerId={selectedCustomerId}
        onClose={handleCloseDrawer}
      />
    </PageTemplate>
  );
}
